import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { confirm } from 'devextreme/ui/dialog';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import {
    DxiColumnComponent,
    DxoFilterRowComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoSearchPanelComponent,
} from 'devextreme-angular/ui/nested';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { escape as _escape, keyBy as _keyBy, remove as _remove } from 'lodash';
import {
    MessageType,
    NotificationService,
    PurchaseOrdersDoc,
    PurchaseOrderService,
    UnifiedQueryV2Service,
    UtilService,
    adminProductUrl,
} from 'src/app/shared/services';

const RESTORE_BACK_TO_ORDERING =
    'ถ้า Restore สินค้านี้จะกลับมาให้สั่งซื้อได้อีก';
const RESTORE_OFF_BOTH_PAGES =
    'ถ้า Restore สินค้านี้จะหายจากหน้านี้ แต่ไม่กลับไปที่หน้า Reordering ' +
    'เพราะหน้านั้นไม่แสดงสินค้าที่ซ่อน listing, ไม่อยู่ในคลังสินค้า หรือเป็นสินค้าแม่';

interface InventoryProduct {
    _id: number;
    sku?: string;
    title?: string;
    remain?: number | null;
    is_show?: boolean;
    has_sub?: boolean;
}

interface LongTermPausedRow {
    pid: number;
    sku: string;
    title: string;
    reason: string | null;
    remain: number | null;
    // Whether the Reordering grid loads this product at all. Without it a
    // restored row has nowhere to come back to.
    orderable: boolean;
}

// The items paused long term, with the reason for each. A reason records a
// decision to stop ordering, so restoring one undoes something the person
// ordering may not have decided themselves.
@Component({
    selector: 'app-long-term-paused',
    templateUrl: './long-term-paused.component.html',
    styleUrls: ['./long-term-paused.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxButtonComponent,
        DxDataGridComponent,
        DxTextBoxComponent,
        DxiColumnComponent,
        DxoFilterRowComponent,
        DxoPagerComponent,
        DxoPagingComponent,
        DxoSearchPanelComponent,
        DxTemplateDirective,
    ],
})
export class LongTermPausedComponent implements AfterViewInit {
    private cdref = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);
    private router = inject(Router);
    private notificationService = inject(NotificationService);
    private purchaseOrderService = inject(PurchaseOrderService);
    private unifiedQueryV2Service = inject(UnifiedQueryV2Service);
    private utilService = inject(UtilService);

    @ViewChild('dataGrid', { static: true })
    private dataGrid!: DxDataGridComponent;

    readonly adminProductUrl = adminProductUrl;
    rows: LongTermPausedRow[] = [];
    searchText = '';
    loading = false;
    saving = false;

    // A load and a restore must not overlap: a load answered after a restore
    // may carry the document from before it, and would put the row back.
    get busy(): boolean {
        return this.loading || this.saving;
    }

    ngAfterViewInit(): void {
        this.load();
        this.cdref.detectChanges();
    }

    load(): void {
        if (this.busy) {
            return;
        }
        this.loading = true;
        this.dataGrid.instance.beginCustomLoading('');
        this.purchaseOrderService
            .getDoc()
            .pipe(
                switchMap((doc) => {
                    const pids = doc.paused_long.map((entry) => entry.pid);
                    // Not limited to shown or non-parent products, unlike the
                    // Reordering grid: hiding a listing must not cost its row
                    // the SKU and title.
                    const products =
                        pids.length === 0
                            ? of([])
                            : this.unifiedQueryV2Service.query<InventoryProduct>(
                                  {
                                      db: 'lnw_crawler',
                                      col: 'inventory',
                                      query: { _id: { $in: pids } },
                                      options: {
                                          projection: {
                                              sku: 1,
                                              title: 1,
                                              remain: 1,
                                              is_show: 1,
                                              has_sub: 1,
                                          },
                                      },
                                  },
                              );
                    return products.pipe(
                        map((found) => this.buildRows(doc, found)),
                    );
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (rows) => {
                    this.rows = rows;
                    this.finishLoading();
                },
                error: (err: unknown) => {
                    this.finishLoading();
                    this.notifyError(err);
                },
            });
    }

    private finishLoading(): void {
        this.loading = false;
        this.dataGrid.instance.endCustomLoading();
    }

    private buildRows(
        doc: PurchaseOrdersDoc,
        products: InventoryProduct[],
    ): LongTermPausedRow[] {
        const productByPid = _keyBy(products, '_id');
        return doc.paused_long.map((entry) => {
            const product: InventoryProduct | undefined =
                productByPid[entry.pid];
            return {
                pid: entry.pid,
                sku: product?.sku ?? `#${entry.pid}`,
                title: product?.title ?? '(ไม่พบในคลังสินค้า)',
                reason: entry.reason ?? null,
                remain: product?.remain ?? null,
                orderable: product?.is_show === true && !product.has_sub,
            };
        });
    }

    backToReordering(): void {
        this.router.navigate(['/secure/utilities/reordering']);
    }

    // Asks first when there is a decision to undo, or when the row will not
    // come back on the Reordering page. Otherwise nothing can surprise anyone.
    // The dialog renders its message as HTML, and product titles hold text
    // like "<5V".
    restore(row: LongTermPausedRow): void {
        if (this.busy) {
            return;
        }
        if (!row.reason && row.orderable) {
            this.persistRestore(row);
            return;
        }
        const sections = [`${_escape(row.sku)} · ${_escape(row.title)}`];
        if (row.reason) {
            sections.push(`พักไว้เพราะ: ${_escape(row.reason)}`);
        }
        sections.push(
            row.orderable ? RESTORE_BACK_TO_ORDERING : RESTORE_OFF_BOTH_PAGES,
        );
        confirm(sections.join('<br><br>'), 'Restore สินค้านี้?').then((ok) => {
            if (ok) {
                this.persistRestore(row);
            }
        });
    }

    // Not cancelled with the page: the user asked for this write, so leaving
    // mid-save must neither abort it nor hide how it ended.
    private persistRestore(row: LongTermPausedRow): void {
        if (this.busy) {
            return;
        }
        this.saving = true;
        this.purchaseOrderService
            .updateDoc((doc) => ({
                ...doc,
                paused_long: doc.paused_long.filter(
                    (entry) => entry.pid !== row.pid,
                ),
            }))
            .subscribe({
                next: () => {
                    this.saving = false;
                    // In place rather than a new array, which would send the
                    // user back to the first page.
                    _remove(this.rows, (other) => other.pid === row.pid);
                    if (!this.destroyRef.destroyed) {
                        this.dataGrid.instance.refresh();
                    }
                    this.notificationService.pushMessage({
                        displayTime: 3000,
                        message: `${row.sku} restored`,
                        messageType: MessageType.success,
                    });
                },
                error: (err: unknown) => {
                    this.saving = false;
                    this.notifyError(err);
                },
            });
    }

    private notifyError(err: unknown): void {
        console.error(err);
        this.notificationService.pushMessage({
            displayTime: 6000,
            message: this.utilService.errorToString(err),
            messageType: MessageType.error,
        });
    }
}
